import { useEffect, useState } from 'react'
import { useGetNotificationStatusesV2 } from './useApiQuery'
import { useInitiatePayment, usePaymentSubmit } from './useApiMutation'
import { PortalNotification } from '../types/portalTypes/PortalNotification'
import { UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { InitiatePaymentRequest, InitiatePaymentResponse } from '../types'
import { apiRoutes } from './apiRoutes'
import { CsError } from './CsError'
import { ContactInfo } from '../components/shared/tasks/earnestMoneyTask/EarnestMoneyContactDialog'

const PAYMENT_ACKNOWLEDGED_POLL_INTERVAL = 10000 // 10 seconds

/*
Flow:  InitiatePayment -> PaymentSubmitted -> PaymentAcknowledge -> PaymentComplete
*/
export function useEarnestMoneyFlow(orderKey: string, notificationId: string, search: string, contactInfo?: ContactInfo, needsContactInfo?: boolean): {
  initiatePaymentMutation: UseMutationResult<InitiatePaymentResponse, CsError, InitiatePaymentRequest, unknown>,
  cachedInitiatePaymentData: InitiatePaymentResponse | undefined,
  isPaymentSubmitted: boolean,
  isPaymentAcknowledged: boolean,
  isPaymentCompleted: boolean,
} {
  const queryClient = useQueryClient()
  const [pollingForAcknowledgedEnabled, setPollingForAcknowledgedEnabled] = useState(false)
  const [shouldQueryNotificationStatuses, setShouldQueryNotificationStatuses] = useState(true)


  const cachedInitiatePaymentData: InitiatePaymentResponse | undefined =
    queryClient.getQueryData<InitiatePaymentResponse>([apiRoutes.initiatePayment(orderKey, notificationId, search)]);

  const notificationStatusesQuery = useGetNotificationStatusesV2(orderKey, notificationId, search, {
    enabled: shouldQueryNotificationStatuses || pollingForAcknowledgedEnabled,
    refetchInterval: pollingForAcknowledgedEnabled ? PAYMENT_ACKNOWLEDGED_POLL_INTERVAL : 0,
    onSuccess: (data: Partial<PortalNotification>) => {
      if (data.IsPaymentAcknowledged && pollingForAcknowledgedEnabled) {
        setPollingForAcknowledgedEnabled(false)
      }
      if (!data.IsPaymentSubmitted) {
        if (!needsContactInfo && !cachedInitiatePaymentData && contactInfo) {

          localStorage.removeItem('$activeInteraction');
          localStorage.removeItem('$k');

          initiatePaymentMutation.mutate({
            isCommercial: contactInfo.isCommercial,
            ...(contactInfo.isCommercial === false && { firstName: contactInfo.firstName, lastName: contactInfo.lastName }),
            ...(contactInfo.isCommercial === true && { entityName: contactInfo.entityName }),
          });
        }
        setShouldQueryNotificationStatuses(false);
      }
      if (data.IsPaymentSubmitted && !data.IsPaymentAcknowledged && !pollingForAcknowledgedEnabled) {
        setPollingForAcknowledgedEnabled(true);
      }
    }
  });

  const initiatePaymentMutation = useInitiatePayment(orderKey, notificationId, search)

  const paymentSubmitMutation = usePaymentSubmit(orderKey, notificationId, search, {
    onSuccess: () => {
      setPollingForAcknowledgedEnabled(true);
    },
  });

  /* Listen for the event from 4tier that the user has submitted payment data.
    When the event is triggered, POST to the notification events that the user has submitted payment data to 4tier. */
  useEffect(() => {

    const handleDepositrSubmit = () => {
      paymentSubmitMutation.mutate(undefined);
    }
    window.addEventListener('deposiTR_submitted', handleDepositrSubmit as EventListener);

    return () => {
      window.removeEventListener('deposiTR_submitted', handleDepositrSubmit as EventListener);
    };
  }, [paymentSubmitMutation])

  const isPaymentSubmitted = !!paymentSubmitMutation.data || !!paymentSubmitMutation.isLoading || !!notificationStatusesQuery.data?.IsPaymentSubmitted
  const isPaymentAcknowledged = !!notificationStatusesQuery.data?.IsPaymentAcknowledged
  const isPaymentCompleted = !!notificationStatusesQuery.data?.IsComplete

  return {
    initiatePaymentMutation,
    cachedInitiatePaymentData,
    isPaymentSubmitted,
    isPaymentAcknowledged,
    isPaymentCompleted,
  }
}
